import React, {useState, useRef, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {
  formatErrors,
  msgTypeMessage,
  aiOriginApproved,
  aiReviewApproved,
  aiReviewDeclined,
  aiDeclineAutoComment,
  aiConfidenceLevel,
  aiConfidencePercent,
  aiConfidenceLabel,
  aiImpressionShown,
  aiImpressionSeen,
  aiSeenDwellMs,
  aiSeenThresholds,
  aiCardVisibleEnough,
  UPDATE_CHAT_AI_CARD_EDIT,
  UPDATE_CHAT_AI_BATCH,
  UPDATE_CHAT_AI_BATCH_CLEAR,
  UPDATE_CHAT_REPLY_MESSAGE
} from '../../../constants';
import {chatMessageAddAction} from '../../../actions/chatAction';
import {aiCardReviewAction, aiImpressionReportAction} from '../../../actions/clientAction';
import {ChatMessageText} from './ChatMessage';

// AI answer card (Design3): shows the confidence and offers apply/edit/decline.
// Only a guard: the card itself is ChatAiCardBody, so that its impression tracking can rely on
// mount/unmount instead of reproducing this condition inside every effect.
export function ChatAiCard({clientId, chatID}) {
  const aiBatch = useSelector(state => state.chat.aiBatch);
  const aiCardEdit = useSelector(state => state.chat.aiCardEdit);

  // while edit mode is on the text lives in the input, so the card itself is not duplicated
  if (!aiBatch || !aiBatch.generated_response || aiCardEdit) {
    return null;
  }

  // A new client or a new batch is a new impression: remounting the body resets the observer and
  // the dwell timer instead of trying to reconcile them mid-flight.
  return <ChatAiCardBody key={String(clientId) + '|' + String(aiBatch.batch_end)}
                         clientId={clientId} chatID={chatID} aiBatch={aiBatch}/>;
}

function ChatAiCardBody({clientId, chatID, aiBatch}) {
  const dispatch = useDispatch();
  const replied = useSelector(state => state.chat.replied);
  const [loading, setLoading] = useState(false);
  const cardRef = useRef(null);
  const batchEnd = aiBatch.batch_end;

  // "shown": the card is in the DOM. The dedupe lives in the action, not here — the card remounts
  // on edit and when a failed send restores the batch, and must not report twice.
  useEffect(() => {
    dispatch(aiImpressionReportAction({
      client_id: clientId, batch_end: batchEnd, event: aiImpressionShown
    }));
  }, [dispatch, clientId, batchEnd]);

  // "seen": at least half the card visible (or filling half the screen) with the tab visible and
  // focused, held continuously for aiSeenDwellMs. Any interruption restarts the clock — two
  // contiguous seconds is a claim we can defend, four flashes of 500ms during scrolling is not.
  // Root is the viewport, not .chat__wrap: the intersection rect is clipped by every ancestor, so
  // a card scrolled out of the feed already reads as not intersecting, and this additionally
  // catches the whole panel being off-screen without threading chatRef down from ChatMessages.
  useEffect(() => {
    const node = cardRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;                       // no observer support: "shown" only, never "seen"
    }
    const mountedAt = Date.now();
    let timer = null;
    let visible = false;
    let ratio = 0;
    let fired = false;

    // blur matters as much as visibilitychange: a curator alt-tabbing to another application
    // leaves the tab "visible", and they are not reading the suggestion either way.
    const active = () => visible && !document.hidden && document.hasFocus();

    // IntersectionObserver reports geometry, not what is on top: modals render through a portal
    // over the still-mounted feed (utils/modal/Modal.js), so a card under an open modal keeps
    // intersecting the viewport in a visible, focused tab. Hit-test the middle of the card's
    // visible part instead — if something else is there, the curator is not looking at us.
    const onTop = () => {
      const rect = node.getBoundingClientRect();
      const top = Math.max(rect.top, 0);
      const bottom = Math.min(rect.bottom, window.innerHeight);
      if (bottom <= top) return false;
      const el = document.elementFromPoint(rect.left + rect.width / 2, (top + bottom) / 2);
      return !!el && node.contains(el);
    };

    const recompute = () => {
      if (fired) return;
      if (active()) {
        if (!timer) {
          timer = setTimeout(() => {
            timer = null;
            if (!active()) return;
            // Occluded: nothing observable fires when a modal closes, so re-arm and look again
            // rather than reporting a suggestion the curator could not actually see.
            if (!onTop()) {
              recompute();
              return;
            }
            fired = true;
            dispatch(aiImpressionReportAction({
              client_id: clientId, batch_end: batchEnd, event: aiImpressionSeen,
              // not the dwell itself — the timer fires at exactly aiSeenDwellMs by construction,
              // so that number carries nothing. This is how long the card waited to be read.
              time_to_seen_ms: Date.now() - mountedAt,
              ratio: Math.round(ratio * 100) / 100, via: 'dwell'
            }));
          }, aiSeenDwellMs);
        }
      } else if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      ratio = entry.intersectionRatio;
      visible = aiCardVisibleEnough(entry);
      recompute();
    }, {root: null, rootMargin: '0px', threshold: aiSeenThresholds});

    observer.observe(node);
    document.addEventListener('visibilitychange', recompute);
    window.addEventListener('focus', recompute);
    window.addEventListener('blur', recompute);

    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
      document.removeEventListener('visibilitychange', recompute);
      window.removeEventListener('focus', recompute);
      window.removeEventListener('blur', recompute);
    };
  }, [dispatch, clientId, batchEnd]);

  // A click is proof the card was read, whether or not the dwell timer got there first: edit in
  // particular unmounts the card and produces no review at all until the message is finally sent,
  // so a curator who reworks the text and then gives up would otherwise look like they never saw
  // it. The action's dedupe makes this a no-op once "seen" has been reported.
  const markSeen = () => {
    dispatch(aiImpressionReportAction({
      client_id: clientId, batch_end: batchEnd, event: aiImpressionSeen, via: 'action'
    }));
  };

  const level = aiConfidenceLevel(aiBatch);
  const percent = aiConfidencePercent(aiBatch);
  const text = aiBatch.generated_response;

  // Drop the Z like the rest of the feed does (ChatMessageTime): chat times are Moscow time,
  // otherwise the card shows an hour offset ahead of the messages next to it.
  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(String(ts).replace('Z', ''));
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return pad(d.getHours()) + ':' + pad(d.getMinutes());
  };

  // empty final_text/comment are stripped by aiCardReviewAction itself
  const reportReview = (status, finalText, comment) => {
    return dispatch(aiCardReviewAction({
      client_id: clientId,
      batch_end: aiBatch.batch_end,
      status,
      final_text: finalText,
      comment
    }));
  };

  const handleApply = () => {
    if (loading) return;
    markSeen();
    setLoading(true);
    // Dismiss the card before the server answers: while the send is in flight the input panel
    // stays unlocked, so a second answer from the curator would follow the first to the client
    // and gamma would get a rejection for an already approved batch. The reply chip is cleared
    // too: the card is sent without a quote link, and a stale chip would thread the next
    // message to the wrong target. The suggestion is restored if the send fails.
    const batch = aiBatch;
    const repliedBefore = replied;
    dispatch({type: UPDATE_CHAT_AI_BATCH_CLEAR, client_id: clientId});
    if (repliedBefore) {
      dispatch({type: UPDATE_CHAT_REPLY_MESSAGE, message: null});
    }
    dispatch(chatMessageAddAction(clientId, chatID, msgTypeMessage, text, null, null, null, false, aiOriginApproved))
      .then(() => {
        setLoading(false);
        reportReview(aiReviewApproved, text).then((res) => {
          if (!res.success) {
            toast.warn('Ответ отправлен, но оценка AI не записана', {position: toast.POSITION.BOTTOM_RIGHT});
          }
        });
      })
      .catch((err) => {
        dispatch({type: UPDATE_CHAT_AI_BATCH, data: batch, client_id: clientId});
        if (repliedBefore) {
          dispatch({type: UPDATE_CHAT_REPLY_MESSAGE, message: repliedBefore});
        }
        setLoading(false);
        toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
      });
  };

  // Decline dismisses the card up front, same as apply: while the request is in flight the input
  // panel stays unlocked, and an answer sent from there would post a second decline for the same
  // batch. Nothing reached the client, so on failure the suggestion comes back and can be retried.
  const handleDecline = () => {
    if (loading) return;
    markSeen();
    setLoading(true);
    const batch = aiBatch;
    dispatch({type: UPDATE_CHAT_AI_BATCH_CLEAR, client_id: clientId});
    reportReview(aiReviewDeclined, null, aiDeclineAutoComment).then((res) => {
      setLoading(false);
      if (!res.success) {
        dispatch({type: UPDATE_CHAT_AI_BATCH, data: batch, client_id: clientId});
        toast.error(res.error ? formatErrors([res.error]) : 'Не удалось отклонить AI-ответ', {position: toast.POSITION.BOTTOM_RIGHT});
      }
    });
  };

  const handleEdit = () => {
    markSeen();
    dispatch({type: UPDATE_CHAT_AI_CARD_EDIT, data: {
      client_id: clientId,
      batch_end: aiBatch.batch_end,
      original_text: text
    }});
  };

  return (
    <div ref={cardRef} className={"ai-card ai-card--" + (level || 'pending')}>
      <div className="ai-card__head">
        <span className="ai-card__dot"></span>
        <span className="ai-card__title">AI-ответ</span>
        <span className="ai-card__badge">
          {aiConfidenceLabel(level)}{percent !== null ? ' · ' + percent + '%' : ''}
        </span>
        {aiBatch.generated_at && <span className="ai-card__time">{formatTime(aiBatch.generated_at)}</span>}
      </div>
      <div className="ai-card__text">
        <ChatMessageText message={text}/>
      </div>
      <div className="ai-card__actions">
        <button type="button" className="btn ai-card__btn ai-card__btn--apply" disabled={loading} onClick={handleApply}>Применить</button>
        <button type="button" className="btn ai-card__btn" disabled={loading} onClick={handleEdit}>Отредактировать</button>
        <button type="button" className="btn ai-card__btn ai-card__btn--decline" disabled={loading} onClick={handleDecline}>Отклонить</button>
      </div>
    </div>
  );
}
